<template>
  <div>
    <SetupPage
        page-label="Conduct"
        eyebrow="ASSESSMENT STRUCTURE"
        title="Conduct"
        description="Maintain the conduct categories used in student evaluations, so behaviour, attitude and participation are graded consistently."
        toolbar-title="Registered conduct"
        :meta="metaLabel"
        :loader="onloader"
    >
        <template slot="side">
            <div v-if="conducts !== null" class="ws-stat">
                <strong>{{ conductItems.length }}</strong>
                <span>{{ conductItems.length === 1 ? "conduct entry" : "conduct entries" }}</span>
            </div>
            <button
                type="button"
                class="btn ws-primary-button"
                title="Add a conduct category"
                @click="openCreate"
            >
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>New conduct</span>
            </button>
        </template>

        <template slot="toolbar">
            <div class="ws-search">
                <i class="fa fa-search" aria-hidden="true"></i>
                <input
                    v-model="searchval"
                    type="search"
                    placeholder="Search conduct"
                    aria-label="Search registered conduct categories"
                    :disabled="!conductItems.length"
                />
                <button
                    v-if="searchval"
                    type="button"
                    class="ws-search__clear"
                    aria-label="Clear search"
                    @click="searchval = ''"
                >
                    <i class="fa fa-times" aria-hidden="true"></i>
                </button>
            </div>
        </template>

        <div v-if="conductsLoading && conducts === null" class="ws-state">
            <i class="fa fa-spinner fa-spin" aria-hidden="true"></i>
            <strong>Loading conduct categories</strong>
            <span>Fetching the conduct entries set up at your school.</span>
        </div>

        <div v-else-if="conductsFailed" class="ws-state">
            <i class="fa fa-exclamation-circle" aria-hidden="true"></i>
            <strong>Conduct could not be loaded</strong>
            <span>Check your connection and try again.</span>
            <button type="button" class="btn ws-ghost-button" @click="retryConduct">
                <i class="fa fa-refresh" aria-hidden="true"></i>
                <span>Try again</span>
            </button>
        </div>

        <div v-else-if="!conductItems.length" class="ws-state">
            <i class="fa fa-list-ul" aria-hidden="true"></i>
            <strong>No conduct entries yet</strong>
            <span>Register the first category, for example “Excellent” or “Needs improvement”.</span>
            <button type="button" class="btn ws-ghost-button" @click="openCreate">
                <i class="fa fa-plus" aria-hidden="true"></i>
                <span>Add conduct</span>
            </button>
        </div>

        <div v-else-if="!filteredConduct.length" class="ws-state">
            <i class="fa fa-search" aria-hidden="true"></i>
            <strong>No conduct entries match “{{ searchval.trim() }}”</strong>
            <span>Try a different conduct name.</span>
            <button type="button" class="btn ws-ghost-button" @click="searchval = ''">
                Clear search
            </button>
        </div>

        <div v-else class="ws-grid" :class="{ 'is-loading': conductsLoading }">
            <article
                v-for="(item, index) in filteredConduct"
                :key="rowKey(item, index)"
                class="ws-card"
            >
                <span class="ws-card__index">{{ index + 1 }}</span>
                <div class="ws-card__body">
                    <span class="ws-card__title">{{ item.ConductName || "Untitled conduct" }}</span>
                </div>
                <div class="ws-card__actions">
                    <button
                        type="button"
                        class="ws-edit-button"
                        :aria-label="editLabel(item)"
                        @click="editConduct(item)"
                    >
                        <i class="fa fa-pencil" aria-hidden="true"></i>
                        <span>Edit</span>
                    </button>
                    <button
                        type="button"
                        class="ws-danger-button"
                        :aria-label="deleteLabel(item)"
                        @click="deleteConduct(item)"
                    >
                        <i class="fa fa-trash" aria-hidden="true"></i>
                        <span>Delete</span>
                    </button>
                </div>
            </article>
        </div>
    </SetupPage>

    <SetupDrawer
        :show.sync="formVisible"
        icon="fa-clipboard-check"
        :eyebrow="editingId ? 'UPDATE RECORD' : 'NEW RECORD'"
        :title="editingId ? 'Edit conduct' : 'Add conduct'"
        :subtitle="editingId ? 'Update this conduct entry.' : 'Add a conduct category used in student evaluations.'"
        :saving="isSaving"
        :submit-label="editingId ? 'Save changes' : 'Create conduct'"
        form-id="conduct-form"
        @close="closeForm"
        @submit="submitConduct"
    >
        <fieldset class="ws-fieldset">
            <legend>Conduct details</legend>

            <div class="row">
                <div class="col-12">
                    <div class="form-group">
                        <label for="conduct-name">
                            Conduct name <span class="ws-required">*</span>
                        </label>
                        <input
                            id="conduct-name"
                            v-model="form.conductName"
                            type="text"
                            class="form-control"
                            :class="{ 'is-invalid': errors.conductName }"
                            placeholder="e.g. Excellent"
                            maxlength="80"
                            autocomplete="off"
                            :disabled="isSaving"
                            @input="clearError('conductName')"
                        />
                        <span v-if="errors.conductName" class="ws-error" role="alert">
                            {{ errors.conductName }}
                        </span>
                        <span v-else class="ws-hint">
                            Shown in the conduct section of student evaluations.
                        </span>
                    </div>
                </div>
            </div>
        </fieldset>
    </SetupDrawer>
  </div>
</template>

<script>
import Alert from "@/mixins/Alert.mixins";
import SetupPage from "@/components/SetupPage";
import SetupDrawer from "@/components/SetupDrawer";

const EMPTY_FORM = () => ({
    conductName: ""
});

export default {
    name: "conduct",
    mixins: [Alert],
    components: {
        SetupPage,
        SetupDrawer
    },
    data() {
        return {
            conducts: null,
            searchval: "",
            conductsLoading: false,
            conductsFailed: false,
            requestSeq: 0,
            formVisible: false,
            isSaving: false,
            editingId: 0,
            form: EMPTY_FORM(),
            errors: {},
            onloader: "none"
        };
    },
    computed: {
        conductItems() {
            return Array.isArray(this.conducts) ? this.conducts : [];
        },
        hasSearch() {
            return this.searchval.trim().length > 0;
        },
        filteredConduct() {
            const tokens = this.searchval
                .toLowerCase()
                .split(/\s+/)
                .filter(Boolean);

            if (!tokens.length) return this.conductItems;

            return this.conductItems.filter(item => {
                const haystack = [item && item.ConductName]
                    .filter(Boolean)
                    .join(" ")
                    .toLowerCase();
                return tokens.every(token => haystack.includes(token));
            });
        },
        metaLabel() {
            if (this.conductsLoading && this.conducts === null) {
                return "Loading conduct entries…";
            }
            if (this.conductsFailed) return "Conduct entries unavailable.";
            if (this.hasSearch) {
                return `${this.filteredConduct.length} of ${this.conductItems.length} shown`;
            }
            const count = this.conductItems.length;
            return `${count} ${count === 1 ? "conduct entry" : "conduct entries"} registered`;
        }
    },
    methods: {
        rowKey(item, index) {
            if (item && item.Id !== undefined && item.Id !== null && item.Id !== "") {
                return item.Id;
            }
            return `conduct-${index}`;
        },
        editLabel(item) {
            return item && item.ConductName ? `Edit ${item.ConductName}` : "Edit conduct";
        },
        deleteLabel(item) {
            return item && item.ConductName ? `Delete ${item.ConductName}` : "Delete conduct";
        },
        errorMessage(error, fallback) {
            const data = error && error.response && error.response.data;
            if (typeof data === "string" && data.trim()) return data.trim();
            if (data && typeof data === "object") {
                return data.message || data.responseMessage || fallback;
            }
            if (error && error.message) return error.message;
            return fallback;
        },
        async loadConduct() {
            const sequence = ++this.requestSeq;
            this.conductsLoading = true;
            this.conductsFailed = false;

            try {
                const resp = await this.$store.dispatch("getconduct");
                if (sequence !== this.requestSeq) return;
                this.conducts = Array.isArray(resp) ? resp : [];
            } catch (error) {
                if (sequence !== this.requestSeq) return;
                this.conductsFailed = true;
                this.conducts = [];
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load the conduct entries."),
                    ""
                );
            } finally {
                if (sequence === this.requestSeq) this.conductsLoading = false;
            }
        },
        retryConduct() {
            this.conductsFailed = false;
            this.conducts = null;
            this.loadConduct();
        },
        resetForm() {
            this.editingId = 0;
            this.errors = {};
            this.form = EMPTY_FORM();
        },
        openCreate() {
            this.resetForm();
            this.formVisible = true;
        },
        async editConduct(item) {
            if (!item || item.Id === undefined || item.Id === null) return;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("getconductbyid", item.Id);
                if (!resp) throw new Error("The selected conduct entry could not be loaded.");

                this.resetForm();
                this.editingId = item.Id;
                this.form = {
                    conductName: resp.ConductName || item.ConductName || ""
                };
                this.formVisible = true;
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to load this conduct entry."),
                    ""
                );
            } finally {
                this.onloader = "none";
            }
        },
        closeForm() {
            this.formVisible = false;
            this.resetForm();
        },
        clearError(field) {
            if (!this.errors[field]) return;
            const errors = { ...this.errors };
            delete errors[field];
            this.errors = errors;
        },
        validate() {
            const errors = {};

            if (!String(this.form.conductName || "").trim()) {
                errors.conductName = "Enter the name of this conduct entry.";
            }

            this.errors = errors;

            if (Object.keys(errors).length) {
                const element = document.getElementById("conduct-name");
                if (element) element.focus();
            }

            return Object.keys(errors).length === 0;
        },
        async submitConduct() {
            if (this.isSaving) return;
            if (!this.validate()) return;

            const isEdit = Boolean(this.editingId);
            const payload = {
                Id: this.editingId || 1,
                ConductName: String(this.form.conductName || "").trim(),
                processop: isEdit ? "U" : "N",
                username: this.$store.state.auth.username,
                accesskey: this.$store.state.auth.accesskey
            };

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("conductregistration", payload);

                if (resp && resp.responseCode && resp.responseCode !== "00") {
                    this.ShowAlertError(
                        resp.responseMessage || "The conduct entry could not be saved.",
                        ""
                    );
                    return;
                }

                this.closeForm();
                this.searchval = "";
                await this.loadConduct();

                if (this.conductsFailed) {
                    this.ShowAlertWarning(
                        "Conduct saved, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess(
                        isEdit
                            ? "Conduct entry updated successfully."
                            : "Conduct entry created successfully.",
                        ""
                    );
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to save this conduct entry at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        },
        async deleteConduct(item) {
            if (!item || this.isSaving) return;
            if (item.Id === undefined || item.Id === null) return;

            const name = item.ConductName || "this conduct entry";
            const result = await this.showSchooldockAlert({
                title: "Delete conduct entry",
                msg: `Delete “${name}”? This cannot be undone.`,
                icon: "warning",
                showCancelButton: true,
                confirmButtonColor: "#c23b4a"
            });
            if (!result || !result.isConfirmed) return;

            this.isSaving = true;
            this.onloader = "flex";
            try {
                const resp = await this.$store.dispatch("getconductbyid", item.Id);
                const payload = {
                    Id: item.Id,
                    ConductName: (resp && resp.ConductName) || item.ConductName || "",
                    processop: "D",
                    username: this.$store.state.auth.username,
                    accesskey: this.$store.state.auth.accesskey
                };

                const response = await this.$store.dispatch("conductregistration", payload);

                if (response && response.responseCode && response.responseCode !== "00") {
                    this.ShowAlertError(
                        response.responseMessage || "The conduct entry could not be deleted.",
                        ""
                    );
                    return;
                }

                await this.loadConduct();

                if (this.conductsFailed) {
                    this.ShowAlertWarning(
                        "Conduct deleted, but the updated list could not be loaded.",
                        ""
                    );
                } else {
                    this.ShowAlertSuccess("Conduct entry deleted successfully.", "");
                }
            } catch (error) {
                this.ShowAlertError(
                    this.errorMessage(error, "Unable to delete this conduct entry at the moment."),
                    ""
                );
            } finally {
                this.isSaving = false;
                this.onloader = "none";
            }
        }
    },
    mounted() {
        const userDetails = this.$store.getters["auth/_user"];
        if (!userDetails || userDetails.isSchoolStaff == false) {
            this.$store.dispatch("auth/Logout");
            this.$router.push("/login");
            return;
        }
        this.loadConduct();
    }
};
</script>
